import {Typography} from '@mui/material';
import FlagButton from '../../FlagButton';
import PrimeButton from '../../PrimeButton';
import makeStyles from '@mui/styles/makeStyles';
import AddToMyListButton from '../../Buttons/AddToMyListButton';
import WatchedButton from '../../Buttons/WatchedButton';
import { memo, useCallback, useMemo }  from "react";
import {entryIsInMyListFor, entryIsWatchedFor, getMetadataItems} from "../../../Utils/Entry";

import "./ListViewEntryListItem.scss";
import RenderIfVisible from '../../Utils/RenderIfVisible';

const useStyles = makeStyles(theme => ({
    primarySection: {
        display: "flex",
        flexGrow: 1, 
        flexWrap: "wrap",
        alignItems: "center",
        [theme.breakpoints.down('sm')]: {
            justifyContent: "space-between",
        }
    },
    entryLink: {
        color: theme.palette.secondary.contrastText,
        textDecoration: 'none',
        padding: ".5rem 0",
        '&:hover': {
            textDecoration: 'none'
        }
    },
}));

export default memo(function ListViewEntryListItem({ linkComponent, entry, categoryId = null, categoryName = null }) {
    const classes = useStyles();

    const awardId = entry.awardId;
    
    const isInMyList = entryIsInMyListFor(entry, categoryId);
    const isWatched = entryIsWatchedFor(entry, categoryId);

    const LinkComponent = linkComponent;

    const PrimeButtonLinkComponent = useCallback((props) => (
        <LinkComponent entry={entry} entryCategoryId={categoryId} {...props} />
    ), [categoryId, entry]);

    const runtime = useMemo(
        () => 
            getMetadataItems(entry.data, entry.judgingMediaType === 'video', entry.hasRuntime)
                ?.find((metaItem) => metaItem.key === 'metadataField3')
                ?.answer,
        [entry],
    );

    const addToMyListButtonLabel = useMemo(() => (
        <div>
            <span className="AddToMyMyListLabel__auxiliary hide show-lg">
                Add To
            </span>
            {" "} {/* force space between the words */}
            <span className="AddToMyMyListLabel__primary">
                My List
            </span>
        </div>
    ), []);

    // console.log("listview categoryId", categoryId);

    return (
        <div className={`ListViewEntryListItem`}>
            <div className="ListViewEntryListItem__section ListViewEntryListItem__section--primary">
                <div className={`${classes.primarySection}`}>
                    <Typography gutterBottom variant="h5" component="h2" className="ListViewEntryListItem__titleheading">
                        <LinkComponent entry={entry} entryCategoryId={categoryId} className={classes.entryLink}>
                            {entry.title}
                            {categoryId && categoryName && ` (${categoryName})`}
                        </LinkComponent>
                    </Typography>
                    <div className="ListViewEntryListItem__metasection">
                        {!entry.isMandatory ? "" : (
                            <div className="ListViewEntryListItem__mandatorywrap">
                                <FlagButton variant="mandatory" />
                            </div>
                        )}
                        <div className="ListViewEntryListItem__metaitems">
                            {entry.certification && (
                                <div>
                                    <FlagButton
                                        variant={entry.certification.answer}
                                    />
                                </div>
                            )}
                            {entry.hasRuntime && (
                                <Typography
                                    gutterBottom
                                    variant="body1"
                                    component="div"
                                    className="ListViewEntryListItem__runtime"
                                >
                                    {runtime}
                                </Typography>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <div className="ListViewEntryListItem__section ListViewEntryListItem__section--actions">
                <RenderIfVisible className="ListViewEntryListItem__actions">
                    <div className="ListViewEntryListItem__actionbtnwrap">
                        <WatchedButton 
                            awardId={awardId}
                            entry={entry}
                            categoryId={categoryId}
                            isOn={isWatched}
                            label={entry.judgingMediaType === 'video' ? "Watched" : "Played"}
                        />
                    </div>
                    <div className="ListViewEntryListItem__actionbtnwrap">
                        <AddToMyListButton
                            awardId={awardId}
                            entry={entry}
                            categoryId={categoryId}
                            isOn={isInMyList}
                            label={addToMyListButtonLabel}
                        />
                    </div>
                    <div className="ListViewEntryListItem__actionbtnwrap hide show-lg">
                        <PrimeButton component={PrimeButtonLinkComponent}>View Entry</PrimeButton>
                    </div>
                </RenderIfVisible>
            </div>
        </div>
    );
});